import { useEffect, useState } from 'react';
import {
  BLENDED_RATE_EXPLANATION,
  RATE_EXPLANATIONS,
  RATE_LABELS,
  type RequiredRateSlug,
} from '@iomi/shared';

// ── Types ────────────────────────────────────────────────────────────────

export interface CrewRatesCardProps {
  /** Label for the card subtitle, e.g. "residential" or "commercial" */
  type: 'residential' | 'commercial';
  supervisorRate: number;
  moverRate: number;
  driverRate: number;
  truckRate: number;
  truckDriverRate: number;
  useBlended: boolean;
  onSupervisorChange: (v: number) => void;
  onMoverChange: (v: number) => void;
  onDriverChange: (v: number) => void;
  onTruckChange: (v: number) => void;
  onTruckDriverChange: (v: number) => void;
  onBlendedChange: (v: boolean) => void;
  /** If true, show a shared-rates info banner */
  sharedRatesBanner?: boolean;
  /** Highlight required rates that are still $0 (after a blocked Continue). */
  showErrors?: boolean;
}

// ── Toggle switch matching admin pattern ────────────────────────────────

function ToggleSwitch({ checked, onChange, label }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer select-none">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent
                   transition-colors duration-200 ease-in-out cursor-pointer
                   focus:outline-none focus:ring-2 focus:ring-plum/20
                   ${checked ? 'bg-plum' : 'bg-stone'}`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm
                     ring-0 transition-transform duration-200 ease-in-out
                     ${checked ? 'translate-x-5' : 'translate-x-0'}`}
        />
      </button>
      <span className="text-[13px] text-midnight">{label}</span>
    </label>
  );
}

// ── Rate row: always an input in the wizard, no edit-mode sequence ───────

function RateInputRow({ slug, rate, onChange, invalid }: {
  slug: RequiredRateSlug;
  rate: number;
  onChange: (v: number) => void;
  invalid: boolean;
}) {
  // Keep the raw text so "4" → "45" types naturally; the parent gets the parsed number on every change.
  const [value, setValue] = useState(rate > 0 ? String(rate) : '');

  useEffect(() => {
    const parsed = parseFloat(value);
    if ((isNaN(parsed) ? 0 : parsed) !== rate) setValue(rate > 0 ? String(rate) : '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate]);

  const inputId = `crew-rate-${slug}`;

  return (
    <div className="py-3 border-b border-stone last:border-b-0">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <label htmlFor={inputId} className="block text-small font-medium text-midnight">
            {RATE_LABELS[slug]}
          </label>
          <p className="text-[12px] text-slate leading-snug mt-0.5">{RATE_EXPLANATIONS[slug]}</p>
        </div>
        <div className="shrink-0 flex items-center gap-1.5">
          <span className="text-small text-slate">$</span>
          <input
            id={inputId}
            type="number"
            inputMode="decimal"
            step="any"
            min="0"
            placeholder="0"
            value={value}
            onChange={e => {
              setValue(e.target.value);
              const num = parseFloat(e.target.value);
              onChange(isNaN(num) || num < 0 ? 0 : num);
            }}
            aria-invalid={invalid || undefined}
            className={`w-24 h-11 px-3 rounded-lg border bg-white text-small text-midnight text-right
                        font-mono tabular-nums placeholder:text-slate
                        focus:outline-none focus:ring-2 focus:ring-plum/20 transition-colors duration-150
                        ${invalid ? 'border-rose focus:border-rose' : 'border-stone focus:border-plum'}`}
          />
          <span className="text-[12px] text-slate">/hr</span>
        </div>
      </div>
      {invalid && (
        <p className="text-[12px] text-rose mt-1.5">Required. Enter a rate above $0.</p>
      )}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────

export function CrewRatesCard({
  type,
  supervisorRate,
  moverRate,
  driverRate,
  truckRate,
  truckDriverRate,
  useBlended,
  onSupervisorChange,
  onMoverChange,
  onDriverChange,
  onTruckChange,
  onTruckDriverChange,
  onBlendedChange,
  sharedRatesBanner,
  showErrors = false,
}: CrewRatesCardProps) {
  // Build the visible rates list based on blended mode
  const visibleRates: { slug: RequiredRateSlug; rate: number; onChange: (v: number) => void }[] = [
    { slug: 'supervisor', rate: supervisorRate, onChange: onSupervisorChange },
    { slug: 'mover', rate: moverRate, onChange: onMoverChange },
    ...(useBlended
      ? [{ slug: 'truck_driver' as const, rate: truckDriverRate, onChange: onTruckDriverChange }]
      : [
          { slug: 'driver' as const, rate: driverRate, onChange: onDriverChange },
          { slug: 'truck' as const, rate: truckRate, onChange: onTruckChange },
        ]),
  ];

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="p-4">
        <span className="font-semibold text-small text-midnight">Crew and Truck Rates</span>
        <p className="text-[12px] text-slate leading-snug mt-0.5">
          Hourly rates for your {type} crew. Every rate below must be above $0 before this step counts as done.
        </p>
      </div>
      <div className="px-4 pb-4 border-t border-stone">
        {sharedRatesBanner && (
          <div className="mt-3 mb-1 rounded-lg bg-plum/5 border border-plum/20 p-3">
            <p className="text-[12px] text-plum">
              Crew rates are shared across commercial and residential quotes. Changes here apply to both.
            </p>
          </div>
        )}
        <div className="pt-3">
          <div className="mb-4">
            <ToggleSwitch
              checked={useBlended}
              onChange={onBlendedChange}
              label="Use blended truck + driver rate"
            />
            <p className="text-[12px] text-slate leading-snug mt-1.5">{BLENDED_RATE_EXPLANATION}</p>
          </div>
          <div>
            {visibleRates.map(rate => (
              <RateInputRow
                key={rate.slug}
                slug={rate.slug}
                rate={rate.rate}
                onChange={rate.onChange}
                invalid={showErrors && rate.rate <= 0}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
